import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Dispatch } from 'redux';
import { IStore } from '../../../../../redux/store';
import { authSelectors } from '../../../../../redux/app/auth';
import moment from 'moment';
import { inspectorSelectors } from 'redux/app/vessel/inspector';
import { vesselSelectors } from 'redux/app/vessel';

type OwnProps = {
    children: React.ReactNode;
	vesselId: string;
};
type Biofouling2CoverPageProps = PropsFromRedux & OwnProps;
function Biofouling2CoverPage(props: Biofouling2CoverPageProps) {
    const {
        dateRange,
        vesselName,
        imoNumber,
        distinctiveNumberOrLetters,
        children,
    } = props;

    return (
        <div>
            {/* First Page */}
            <div className="flex flex-col justify-between px-8 py-4 mb-2">
                <div className="mx-0 my-24 text-xl font-medium text-center">
                    <h3 className="uppercase">
                        PART II - Monitoring of biofouling risk parameters
                    </h3>
                </div>
                <div className="flex justify-center mt-8 mb-32">
                    <div className="w-2/3 mt-2.5">
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mb-0 mr-2 min-w-fit">Name of ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0">
                                {vesselName}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mb-0 mr-2 min-w-fit">
                                Distinctive number or letters:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {distinctiveNumberOrLetters}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mb-0 mr-2 min-w-fit">IMO Number:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0">
                                {imoNumber}
                            </p>
                        </div>

                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mb-0 mr-2 min-w-fit">
                                Gross tonnage:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {props?.tonnage?.grossTonnage || ''}
                            </p>
                        </div>
                        <div
                            className="flex items-center justify-between mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <div
                                style={{ width: '54%' }}
                                className="flex items-center mb-2"
                            >
                                <p className="mr-2 min-w-fit">Period from :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.startDate &&
                                        moment(dateRange?.startDate).format(
                                            'DD-MM-yyyy'
                                        )}{' '}
                                </p>
                            </div>
                            <div
                                style={{ width: '45%' }}
                                className="flex items-center mb-2"
                            >
                                <p className="mr-2 min-w-fit">to :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.endDate &&
                                        moment(dateRange?.endDate).format(
                                            'DD-MM-yyyy'
                                        )}
                                </p>
                            </div>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">Working language of the ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                English
                            </p>
                        </div>
                    </div>
                </div>

                <div className="border p-1 my-4 border-[#2D3748]">
                    Note: Biofouling Record Book Part II should be provided to
                    every ship with a Biofouling Management Plan, to record when
                    the ship is at higher risk of biofouling accumulation given
                    by monitoring of biofouling risk parameters. Relevant
                    contingency actions should also be recorded
                </div>
            </div>
            {/* Second Page */}
            <div className="flex flex-col justify-between px-8 py-4 pt-0 mb-2 text-left newpage">
                <h2 className="text-xl font-medium">Introduction</h2>
                <p className="py-2">
                    The following pages of this section show a comprehensive
                    list of risk parameters to be monitored and recorded in
                    Biofouling Record Book Part II whenever the risk is
                    increased according to the BFMP. The items have been grouped
                    into sections, each of which is denoted by a letter code
                </p>
                <p className="py-2">
                    When making entries in Biofouling Record Book Part II, the
                    date, code and item number should be inserted in the
                    appropriate columns and the required particulars should be
                    recorded chronologically in the blank spaces. Each completed
                    operation should be signed for and dated by the officer or
                    officers in charge. The master of the ship should sign each
                    completed page.
                </p>
                <p className="py-2">
                    The use of an electronic record book to record when the ship
                    is subject to higher risk of biofouling accumulation is an
                    alternative method to a hard copy record book. Electronic
                    recording and reporting should be encouraged as it may have
                    many benefits and may allow ships to utilize technology to
                    monitor the risk parameters as defined in the BFMP. This may
                    reduce administrative burdens and contribute to better
                    surveillance of potential risk. In case electronic recording
                    is to be used whenever the ship has higher risk, resolution
                    MEPC.312(74) may be used for guidance
                </p>
                <p className="py-2">
                    Biofouling Record Book Part II may contain many references
                    to contingency actions. When actions include inspection,
                    maintenance and/or cleaning, these may be recorded in
                    Biofouling Record Book Part I.
                </p>
                <p className="py-2">
                    Biofouling Record Book Part II should be kept on board the
                    ship in a place where it is readily available for inspection
                    at all reasonable times and for the life of the ship.
                </p>
                <p className="py-2">
                    Any inspection of Biofouling Record Book Part II should be
                    performed as expeditiously as possible without causing the
                    ship to be unduly delayed.
                </p>
            </div>
            {/* Third Page */}
            <div className="px-8 py-0 newpage">
                <h2 className="mb-6 text-2xl font-medium">
                    LIST OF ITEMS TO BE RECORDED
                </h2>
                {/* <!-- Section A --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">
                        (A) When the ship operates outside the expected
                        operating profile specified in the BFMP (e.g. speed,
                        temperature or salinity)
                    </h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Duration and dates when ship is not operating in
                            line with its BFMP.
                        </li>
                        <li>Reason for departure from normal operation.</li>
                        <li>
                            Contingency actions taken to minimize biofouling
                            accumulation (e.g. more frequent inspections) taken
                            in the period when the ship is operating outside the
                            expected operating profile.
                        </li>
                        <li>
                            Time and location (port name or latitude/longitude)
                            when the ship operates again as specified in the
                            BFMP.
                        </li>
                    </ol>
                </div>

                {/* <!-- Section B --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">
                        (B) Maintenance/service or damage to AFC
                    </h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Date/period and description of any observed
                            reduction of the efficacy, damage or deviation from
                            maintenance/service to anti-fouling coating (AFC)
                            during its lifetime.
                        </li>
                        <li>
                            Date/period and description of any operation beyond
                            expected lifetime.
                        </li>
                        <li>
                            Contingency actions taken to minimize biofouling
                            accumulation (e.g. more frequent inspections).
                        </li>
                        <li>
                            Date/period and location where any AFC maintenance
                            or repair was performed (e.g. in dry dock).
                        </li>
                        <li>
                            Description of any AFC, including patch repairs,
                            that was applied during maintenance. Detail the type
                            of AFC, the area and locations it was applied to
                            (including the location of dry-dock support blocks
                            if relevant), an estimated percentage cover of
                            reapplication of the AFC, the coating thickness
                            achieved and any surface preparation work undertaken
                            (e.g. complete removal of underlying AFC or
                            application of new AFC over the top of existing
                            AFC).
                        </li>
                        <li>
                            Reference to any supporting data for AFC maintenance
                            (e.g. AFC technical file).
                        </li>
                        <li>
                            Name, position and signature of the person in charge
                            of the activity.
                        </li>
                    </ol>
                </div>

                {/* <!-- Section C --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">
                        (C) Maintenance/service or downtime/malfunction of MGPS
                    </h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Date/period and description of any observed
                            reduction of the efficacy, downtime, malfunction or
                            deviation from maintenance/service of marine growth
                            prevention system (MGPS) during its lifetime.
                        </li>
                        <li>
                            Date/period and description of operation beyond the
                            expected lifetime.
                        </li>
                        <li>
                            Date and location of any instances when the system
                            was not operating in line with the BFMP.
                        </li>
                        <li>
                            Records of maintenance (including regularly
                            monitoring the electrical and mechanical functions
                            of the systems, calibration, or adjustment of
                            treatment dosages).
                        </li>
                        <li>
                            Contingency actions taken to minimize biofouling
                            accumulation (e.g. more frequent inspections).
                        </li>
                        <li>
                            Name, position and signature of the person in charge
                            of the activity.
                        </li>
                    </ol>
                </div>
            </div>
            {/* Fourth Page */}
            <div className="px-8 py-0 mt-4 newpage">
                {/* <!-- Section D --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">
                        (D) Maintenance/service or downtime/malfunction of other
                        AFS
                    </h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Date/period and description of any observed
                            reduction of the efficacy, downtime, malfunction or
                            deviation from maintenance/service of other AFS
                            during its lifetime.
                        </li>
                        <li>
                            Date/period and description of operation beyond
                            expected lifetime.
                        </li>
                        <li>
                            Date and location of any instances when the system
                            was not operating in line with the Biofouling
                            Management Plan.
                        </li>
                        <li>Records of maintenance.</li>
                        <li>
                            Contingency actions taken to minimize biofouling
                            accumulation (e.g. more frequent inspections).
                        </li>
                    </ol>
                </div>

                {/* <!-- Section E --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">
                        (E) Deviation from regular use of expected proactive
                        cleaning as specified in the BFMP
                    </h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Date and location where ship did not conduct
                            proactive cleaning as specified.
                        </li>
                        <li>
                            Contingency actions taken to minimize biofouling
                            accumulation (e.g. inspections of biofouling and/or
                            reactive cleaning before return to proactive
                            cleaning activity).
                        </li>
                        <li>Records of maintenance, if any.</li>
                        <li>
                            Date when ship returned to normal activities with
                            proactive cleaning.
                        </li>
                    </ol>
                </div>

                {/* <!-- Section F --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">
                        (F) Deviation from necessary reactive cleaning as
                        specified in the BFMP
                    </h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Date and location where ship was inspected and
                            reactive cleaning found necessary.
                        </li>
                        <li>
                            Contingency actions taken until reactive cleaning,
                            including scheduling of reactive cleaning activity.
                        </li>
                        <li>
                            Date when ship completed the reactive cleaning and
                            reference to relevant recording in Part I.
                        </li>
                    </ol>
                </div>
            </div>
            {/* Fifth page */}
            <div className="px-8 py-4 mt-4 newpage">
                {/* <!-- Section G --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">
                        (G) When the ship is idle (berthed, anchored, moored)
                        for a longer period
                    </h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Date and location where ship was laid up, including
                            general description of biofouling pressure, e.g.
                            temperature and distance to the coastline.
                        </li>
                        <li>
                            Contingency actions taken to minimize biofouling
                            accumulation (e.g. inspections, sea chests blanked
                            off or short voyages taken prior to and following
                            the period laid up).
                        </li>
                        <li>
                            Precautions taken to minimize biofouling
                            accumulation (e.g. short voyage).
                        </li>
                        <li>Date when ship returned to normal operations.</li>
                    </ol>
                </div>

                {/* <!-- Section H --> */}
                <div className="mb-8">
                    <h3 className="mb-2 font-bold">
                        (H) When the ship has performance loss as per
                        Performance Monitoring System for a period beyond the
                        expected period as specified in the BFMP
                    </h3>
                    <ol className="pl-6 space-y-2 list-decimal">
                        <li>
                            Date and location where ship started with
                            performance loss beyond the expectations.
                        </li>
                        <li>
                            Inspections or biofouling management actions taken
                            prior to and following the period with performance
                            loss.
                        </li>
                        <li>
                            Contingency actions taken to minimize biofouling
                            accumulation.
                        </li>
                        <li>Date when ship returned to normal performance.</li>
                    </ol>
                </div>

                {/* <!-- Section I --> */}
                <div className="mb-4">
                    <h3 className="mb-2 font-bold">(I) Other deviations</h3>
                    <p className="italic text-gray-600">
                        This section is for recording any other deviations not
                        covered in the previous sections.
                    </p>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
    const inspectorStore = inspectorSelectors._getStore(store);
    const dateRange = inspectorSelectors.getDateRange(inspectorStore);
	const vesselStore = vesselSelectors._getStore(store);
	const vesselInfo = vesselSelectors.getVesselById(
		ownProps.vesselId,
		vesselSelectors.getVessels(vesselStore)
	);
    return {
        dateRange,
        vesselName: vesselInfo.vesselName,
        imoNumber: vesselInfo.imoNumber,
        tonnage: vesselInfo.tonnage,
        distinctiveNumberOrLetters: vesselInfo?.distinctiveNumberOrLetters,
    };
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
    return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(Biofouling2CoverPage);
